import cloneDeep from 'lodash/cloneDeep';

import {
  Entry,
  isCohortEntry,
  isEventBasedEntry,
  isEventEntry,
  isFormulaEntry,
  isGroupedEntry,
  isInlineFormulaEntry,
  isMetricEntry,
  isUserBasedEntry,
  MetricEntry,
  QueryEntry,
  QueryEventEntry,
  QueryFormulaEntry,
  QuerySegmentEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {
  FUNNELS_MATH_TYPES,
  MATH_TYPE,
  PRIMITIVE_MATH_TYPES,
  ResourceType,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {
  EventSegment,
  isCohortSegment,
  isEventSegment,
  isUserSegment,
  OtherSegmentation,
  Segment,
  SegmentValue,
} from 'common/query-builder/query-block/segment-menu/types';
import {ButtonTheme} from 'common/components/button/constants';
import {SelectTheme} from 'common/components/select/constants';
import {getReportName} from 'common/report/util';
import {Segmentation} from 'common/types/reports/query';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {isBehaviorFilter, isCohortFilter, isUserFilter} from 'common/widgets/property-filter-menu/models/utils';
import {
  isBehaviorSegmentation,
  isMultiAttributionSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {MultiAttribution, MultiAttributionType} from 'common/widgets/multi-attribution-menu/types';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import {BEHAVIOR_MULTI_ATTRIBUTION_FILTER} from 'common/query-builder/query-block/properties-menu/constants';
import {MetricType} from 'common/query-builder/query-block/constants';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import {getEntries} from 'common/report/insights/util/events';
import {getResolvedFormulaEntry} from 'common/util/formulas';
import {InsightsResourceType} from 'common/report/insights/util/types';

export const queryBuilderUtils = {
  isCoreReport,
};

/**
 * Sanitizes exclusionSteps by fixing out of range step selections
 */
export function sanitizeExclusionSteps(
  exclusionSteps: QueryEventEntry[],
  events: Array<QueryEntry | MetricEntry>,
): QueryEventEntry[] {
  return exclusionSteps.map((step) => {
    if (`to` in step.excludeRange && (step.excludeRange as {from: number; to: number}).to > events.length) {
      step.excludeAll = true;
      step.excludeRange = null;
    }

    return step;
  });
}

// The format used by mp-query-builder and mp-query-entry-section are different. So it's necessary to map
// between these two formats.
export function transformToQuerySegmentEntries(segments: Segment[]): QuerySegmentEntry[] {
  return cloneDeep(segments).map((segment) => {
    let customBucket;
    let segmentValue: SegmentValue;

    if (isCohortSegment(segment)) {
      segmentValue = segment;
    } else if (Array.isArray(segment)) {
      // Value comparisons shouldnt have custom bucketing so dont need to remove
      segmentValue = segment.map((seg) => Object.values(seg)[0]);
    } else {
      const segmentation: Segmentation = Object.values(segment)[0];
      customBucket = segmentation.customBucket;
      delete segmentation.customBucket;
      // The QuerySegmentEntry format has the `customBucket` as a top-level field. So we remove `customBucket`
      // from the segment and move it to be a top-level field.
      segmentValue = segmentation;
    }

    return {
      event: segmentValue,
      filters: [],
      type: SelectorTypeDefinitions.Segment,
      ...(customBucket ? {customBucket} : {}),
    };
  });
}

/**
 * Insert the custom bucketing entry field into the segment fields
 * @todo [pablo] refactor this so that query-entry-section looks at the
 * segment value to see if custom bucketing is enabled or not
 */
export function transformFromQuerySegmentEntries(entries: QuerySegmentEntry[]): Segment[] {
  return entries.map((entry) => {
    if (isCohortSegment(entry.event)) {
      return entry.event;
    }

    const segmentationArray = [].concat(entry.event).map((event) => ({
      ...event,
      ...(entry.customBucket ? {customBucket: entry.customBucket} : {}),
    }));
    const segmentations = segmentationArray.map((segment) => {
      switch (segment.resourceType) {
        case ResourceType.Event:
          return {[ResourceType.Event]: segment};
        case ResourceType.User:
          return {[ResourceType.User]: segment};
        case ResourceType.Other:
          return {[ResourceType.Other]: segment as OtherSegmentation};
        case ResourceType.Cohort:
        default:
          throw new Error(`unknown segment type: ${segment.resourceType}`);
      }
    });
    return segmentations.length === 1 ? segmentations[0] : segmentations;
  });
}

function hasSupportedAttributionCountType(countType: MATH_TYPE): boolean {
  return [PRIMITIVE_MATH_TYPES.TOTAL, PRIMITIVE_MATH_TYPES.UNIQUE].includes(countType as PRIMITIVE_MATH_TYPES);
}

function hasSupportedAttributionType(type: MultiAttributionType): boolean {
  // These attribution types are compatible with all count types
  return [MultiAttributionType.LastTouch, MultiAttributionType.FirstTouch, MultiAttributionType.Participation].includes(
    type,
  );
}

export function isSupportedMultiAttributionType(countType: MATH_TYPE, type: MultiAttributionType): boolean {
  // Total Sessions is never supported.
  if (
    countType === PRIMITIVE_MATH_TYPES.SESSIONS ||
    countType === FUNNELS_MATH_TYPES.SESSION ||
    countType === FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION
  ) {
    return false;
  }

  // There are a set of attribution types that are compatible with all count
  // types and a set of count types that are compatible with all attribution
  // types. If they have one of either and they're good, if they don't then the
  // pairing is incompatible.
  return hasSupportedAttributionCountType(countType) || hasSupportedAttributionType(type);
}

/**
 * Returns true if the given query/metric entry supports the given multi-attribution type.
 *
 * If no multi attribution type is provided it will default to the default assigned
 * when adding a multi attribution breakdown.
 * */
export function isSupportedMultiAttributionTypeForEntry({
  entry,
  multiAttributionType = DefaultMultiAttribution.type,
  metricsResource,
}: {
  entry: QueryEntry | MetricEntry | QueryFormulaEntry;
  multiAttributionType?: MultiAttributionType;
  metricsResource: MetricsResource;
}): boolean {
  if (isFormulaEntry(entry)) {
    if (isInlineFormulaEntry(entry)) {
      return false;
    }
    const resolvedFormula = getResolvedFormulaEntry({metricsResource, formulaEntry: entry});
    return Boolean(
      resolvedFormula?.event.referencedMetrics.every((metric) => {
        return (
          metric.behavior.resourceType === InsightsResourceType.Events &&
          isSupportedMultiAttributionType(metric.measurement.math, multiAttributionType)
        );
      }),
    );
  } else if (
    (isMetricEntry(entry) &&
      [MetricType.Funnel, MetricType.Retention, MetricType.RetentionFrequency].includes(entry.behavior.type)) ||
    isEventEntry(entry)
  ) {
    return isSupportedMultiAttributionType(getMathTypeFromEntry(entry), multiAttributionType);
  }
  return false;
}

export function isMultiAttributionFilter(filter: PropertyFilter): boolean {
  return filter.propertyName === BEHAVIOR_MULTI_ATTRIBUTION_FILTER.value;
}

export function isMultiAttributionSegment(segment: Segment): segment is EventSegment {
  return isEventSegment(segment) && isMultiAttributionSegmentation(segment.event);
}

function isBehaviorSegment(segment: Segment) {
  return isUserSegment(segment) && isBehaviorSegmentation(segment.user);
}

export function getMathTypeFromEntry(entry: Entry): MATH_TYPE {
  if (isMetricEntry(entry)) return entry.measurement.aggregationOperator;
  return entry.aggregationOperator;
}

export function getMultiAttributionFromEntry(entry: Entry): MultiAttribution {
  if (isMetricEntry(entry)) return entry.measurement.multiAttribution;
  if (isEventEntry(entry)) return entry.multiAttribution;
  if (isFormulaEntry(entry)) return entry.multiAttribution;
  return null;
}

/**
 * Sanitizes group-by entries when a cohort entry is selected
 */
export function sanitizeGroupBys({
  entries,
  segments,
  metricsResource,
}: {
  entries: Array<QueryEntry | MetricEntry>;
  segments: Segment[];
  metricsResource: MetricsResource;
}) {
  const allEntries = flattenReferencedEntries(entries, metricsResource);
  const nonFormulaEntries = allEntries.filter((entry) => !isFormulaEntry(entry));
  const eventsOnlyEntries = nonFormulaEntries.every((entry) => isEventBasedEntry(entry) || isGroupedEntry(entry));
  const peopleOnlyEntries = nonFormulaEntries.length > 0 && nonFormulaEntries.every((entry) => isUserBasedEntry(entry));
  const hasCohort = entries.length > 0 && entries.some((entry) => isCohortEntry(entry));
  const hasXau = nonFormulaEntries.some((entry) => isXauMathType(getMathTypeFromEntry(entry)));
  let results = segments;

  if (peopleOnlyEntries || !eventsOnlyEntries) {
    results = results.filter(
      (segment) => isCohortSegment(segment) || (isUserSegment(segment) && !isBehaviorSegmentation(segment.user)),
    );
  }
  if (hasXau) {
    results = results.filter((segment) => !(isBehaviorSegment(segment) || isMultiAttributionSegment(segment)));
  }
  if (hasCohort) {
    results = results.filter((segment) => isCohortSegment(segment));
  }

  const nonInlineFormulaEntries = entries.filter((entry) => !isInlineFormulaEntry(entry));
  const everyMultiAttributionEventIsSupported = nonInlineFormulaEntries.every((entry) => {
    // We can ignore the case where entry.multiAttribution is blank. This is the
    // case when we are adding the multi attribution breakdown for the first
    // time, in which case we'll only add a compatible multi attribution type.
    const multiAttribution = getMultiAttributionFromEntry(entry);
    if (multiAttribution) {
      return isSupportedMultiAttributionTypeForEntry({
        entry,
        multiAttributionType: multiAttribution.type,
        metricsResource,
      });
    }
    return true;
  });

  // For multi attribution we want to remove the entire breakdown if any event
  // is not supported in the query.
  results = !everyMultiAttributionEventIsSupported
    ? results.filter((segment) => !isMultiAttributionSegment(segment))
    : results;

  return results;
}

/**
 * Sanitizes filter entries when a cohort entry is selected
 */
export function sanitizeFilters(events: Array<QueryEntry | MetricEntry>, filters: PropertyFilter[]) {
  const eventsOnlyEntries = events.every((entry) => isEventBasedEntry(entry) || isGroupedEntry(entry));
  const peopleOnlyEntries = events.length > 0 && events.every((entry) => isUserBasedEntry(entry));
  const hasXau = events.some((entry) => isXauMathType(getMathTypeFromEntry(entry)));
  let results = filters;
  if (hasXau) {
    results = filters.filter((segment) => !isBehaviorFilter(segment));
  }

  if (peopleOnlyEntries || !eventsOnlyEntries) {
    // TODO remove behavior stuff once it is no longer user resource type
    // only keep cohort filter and user property filters
    results = results.filter(
      (segment) => isCohortFilter(segment) || (isUserFilter(segment) && !isBehaviorSegmentation(segment)),
    );
  }

  return results;
}

export type MismatchedFilterAndSegment = {
  filterLabel: string;
  segmentLabel: string;
};

/**
 * Ensure that any date range changes in the multi attribution breakdown are
 * applied to the filters.
 *
 * Remove filters that break the invariant that the multi attribution filter and
 * breakdown must have the same property.
 *
 * Returns filters with mismatched filters removes. It also return the pair of
 * labels corresponding to the first found mismatched filter and segment.
 */
export function sanitizeMultiAttributionFilter(
  filters: PropertyFilter[],
  groupBy: Segment[],
): {
  results: PropertyFilter[];
  mismatchedFilterAndSegment: MismatchedFilterAndSegment | null;
} {
  const multiAttributionSegment = groupBy.find((segment) => isMultiAttributionSegment(segment)) as EventSegment;

  let results = filters.map((filter) => {
    return isMultiAttributionFilter(filter)
      ? Object.assign({}, filter, {
          behavior: {...filter.behavior, dateRange: multiAttributionSegment[ResourceType.Event].behavior.dateRange},
        })
      : filter;
  });
  const multiAttributionFilter = filters.find((filter) => isMultiAttributionFilter(filter));
  let mismatchedFilter = null;
  if (
    multiAttributionSegment &&
    multiAttributionFilter &&
    multiAttributionSegment[ResourceType.Event].behavior?.property.value !==
      multiAttributionFilter?.behavior?.property.value
  ) {
    results = results.filter((filter) => !isMultiAttributionFilter(filter));
    mismatchedFilter = {
      filterLabel: multiAttributionFilter?.behavior?.property.label,
      segmentLabel: multiAttributionSegment[ResourceType.Event].behavior?.property.label,
    };
  }

  return {
    results,
    mismatchedFilterAndSegment: mismatchedFilter,
  };
}

function isCoreReport() {
  return [`dashboards`, `funnels`, `insights`, `retention`, `flows`].includes(getReportName());
}

/** TODO(pablo): drop this method once internal testing is completed */
export const qbStyleAttrs = (forNewUI = null) => ({
  selectorTheme: forNewUI ?? isCoreReport() ? SelectTheme.QueryBuilder : SelectTheme.Select,
  selectorType: `button`,
  buttonTheme: forNewUI ?? isCoreReport() ? ButtonTheme.Tertiary : ButtonTheme.Select,
});

export function isXauMathType(value: MATH_TYPE): value is XAU_MATH_TYPES {
  return Object.values(XAU_MATH_TYPES).includes(value as XAU_MATH_TYPES);
}

export function getFunnelStepCount(entries: Entry[]): number | undefined {
  const funnelEntries = entries.filter((entry) => isMetricEntry(entry) && entry.behavior.type === MetricType.Funnel);
  if (funnelEntries.length > 0) {
    const funnelBehaviors = funnelEntries.map((entry) => (entry as MetricEntry).behavior.behaviors);
    const funnelLengths = funnelBehaviors.map((behaviors) => behaviors.length);
    return Math.min(...funnelLengths);
  }
  return undefined;
}

/** Get entries with any saved formula referencedMetrics appended */
export function flattenReferencedEntries(
  entries: Array<QueryEntry | MetricEntry>,
  metricsResource: MetricsResource,
): Array<QueryEntry | MetricEntry> {
  if (!entries?.length) {
    return entries;
  }

  const formulasReferencingMetrics: QueryFormulaEntry[] = entries.filter((entry): entry is QueryFormulaEntry => {
    return isFormulaEntry(entry) && Boolean(entry.event.id || entry.event.referencedMetrics);
  });
  const entriesFromFormulas = formulasReferencingMetrics
    .map((formulaEntry) => {
      const resolvedEntry = getResolvedFormulaEntry({formulaEntry, metricsResource});
      return resolvedEntry && getEntries({showClauses: resolvedEntry.event.referencedMetrics || []});
    })
    .filter(Boolean)
    .flat();

  return [...entries, ...entriesFromFormulas];
}
